import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from "react";
import { useLazyLoadQuery } from "react-relay";

import { faChevronLeft, faChevronRight, faVideoSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faRecordSlash } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { addWeeks, endOfDay, roundToNearestMinutes } from "date-fns";
import { compact } from "lodash";
import { commitMutation, graphql } from "relay-runtime";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { meetingDateFormat } from "~/lib/utils";
import { RecordIconSwitch } from "~/meetingRecorder/RecordIcon";
import { environment } from "~/models";
import { recurrenceRuleToString } from "~/stream/utils";
import { Button, Tooltip } from "~/ui";
import Link from "~/ui/Link";

import mockNotesImg from "./css/manual-record-select.png";
import { ReactComponent as WillBeRecordedIcon } from "./images/will-be-recorded.svg";
import { JourneyContext } from "./JourneyContext";

import type {
	ManualRecordSelectPageQuery,
	ManualRecordSelectPageQuery$data,
} from "~/__generated__/ManualRecordSelectPageQuery.graphql";

import styles from "./css/ManualRecordSelectPage.module.css";

export const RECORD_MEETING_PATH = "/o/record-meeting/";

const LOCAL_STORAGE_USER_TOGGLED_GUIDS_KEY = "manual-record-select-page-user-toggled-guids";

let initialGuidsToggled: Set<string>;
try {
	initialGuidsToggled = new Set<string>(JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_TOGGLED_GUIDS_KEY) || "[]"));
} catch {
	initialGuidsToggled = new Set<string>();
}

const ManualRecordSelectPageMutation = graphql`
	mutation ManualRecordSelectPageMutation($eventGuid: String!, $value: Boolean!) {
		toggleEventSchedulingMeetingAssistantAutomation(eventGuid: $eventGuid, value: $value) {
			event {
				id
				guid
				willBeRecorded(noCache: true)
			}
		}
	}
`;

type BaseEvent = Omit<NonNullable<ManualRecordSelectPageQuery$data["events"]["edges"][0]>["node"], " $fragmentSpreads">;

function ManualRecordSelectEvent(props: {
	event: BaseEvent;
	viewer: ManualRecordSelectPageQuery$data["viewer"];
	userToggledGuids: Set<string>;
	updateUserToggledGuids: Dispatch<SetStateAction<Set<string>>>;
}) {
	const { event, viewer, userToggledGuids, updateUserToggledGuids } = props;

	const onCheckedChange = React.useCallback(async () => {
		if (
			event.willBeRecorded == "RESTRICTED" ||
			(event.willBeRecorded == "YES" && !userToggledGuids.has(event.guid)) ||
			event.conferenceCalls.length === 0
		)
			return;
		const eventWillBeRecorded = !(event.willBeRecorded == "YES");

		if (eventWillBeRecorded) {
			updateUserToggledGuids((prev: Set<string>) => new Set([...prev, event.guid]));
		} else {
			updateUserToggledGuids((prev: Set<string>) => {
				prev.delete(event.guid);
				return new Set(prev);
			});
		}

		commitMutation(environment, {
			mutation: ManualRecordSelectPageMutation,
			variables: { eventGuid: event.guid, value: eventWillBeRecorded },
			optimisticUpdater(store) {
				const eventRecord = store.get<BaseEvent>(event.id);
				if (!eventRecord) return;

				if (eventWillBeRecorded) {
					const viewerRecord = store.get(viewer.user.id);
					if (viewerRecord) {
						eventRecord.setValue("YES", "willBeRecorded", { noCache: true });
					}
				} else {
					eventRecord.setValue("NO", "willBeRecorded", { noCache: true });
				}
			},
		});
	}, [
		event.guid,
		event.id,
		event.willBeRecorded,
		event.conferenceCalls,
		viewer.user.id,
		userToggledGuids,
		updateUserToggledGuids,
	]);

	let recordingIcon;
	let tooltipContent;
	let canToggle = false;

	if (event.willBeRecorded == "RESTRICTED") {
		recordingIcon = <FontAwesomeIcon icon={faRecordSlash} className={styles.noCallIcon} />;
		tooltipContent = "AI Notes for this meeting is restricted by the workspace";
	} else if (event.willBeRecorded == "YES") {
		if (!userToggledGuids.has(event.guid)) {
			recordingIcon = <WillBeRecordedIcon className={styles.willBeRecorded} />;
			tooltipContent = "Fellow will take AI Notes for this meeting";
		} else {
			recordingIcon = <RecordIconSwitch checked className={styles.toggle} />;
			tooltipContent = "Fellow will take AI Notes for this meeting";
			canToggle = true;
		}
	} else if (event.conferenceCalls.length === 0) {
		recordingIcon = <FontAwesomeIcon icon={faVideoSlash} className={styles.noCallIcon} />;
		tooltipContent = "Taking AI Notes requires a video call";
	} else if (event.willBeRecorded == "NO") {
		recordingIcon = <RecordIconSwitch checked={false} className={styles.toggle} />;
		tooltipContent = "Turn on AI Notes for this meeting";
		canToggle = true;
	} else {
		recordingIcon = <></>;
		tooltipContent = null;
	}

	const iconId = `signup-record-meeting-icon-container-${event.id}`;

	const boundingClientRect = useCallback(
		() => document.getElementById(iconId)?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0),
		[iconId],
	);
	return (
		<Tooltip
			disabled={!tooltipContent}
			className={styles.tooltip}
			content={tooltipContent}
			maxWidth={160}
			getReferenceClientRect={boundingClientRect}
		>
			<div
				key={event.id}
				className={cx(styles.eventContainer, { [styles.canToggle]: canToggle })}
				onClick={onCheckedChange}
			>
				<div className={styles.left}>
					<div className={styles.title}>{event.title}</div>
					{event?.recurrenceRule?.length && event?.recurrenceRule[0] ? (
						<p className={styles.eventDurationText}>Repeats {recurrenceRuleToString(event?.recurrenceRule)}</p>
					) : (
						<p className={styles.eventDurationText}>
							{meetingDateFormat(event?.start || new Date(), event?.end || new Date(), Boolean(event?.isAllDay))}
						</p>
					)}
				</div>
				<div className={styles.right}>
					<div id={iconId}>{recordingIcon}</div>
				</div>
			</div>
		</Tooltip>
	);
}

export function ManualRecordSelect() {
	const { nextStep } = React.useContext(JourneyContext);
	const [eventsPage, setEventsPage] = React.useState(1);
	const [userToggledGuids, setUserToggledGuids] = React.useState<Set<string>>(initialGuidsToggled);

	const onNotNowClick = useCallback(() => {
		dispatchAnalyticsTrack("[ManualRecordSelect] Skipped");
		nextStep();
	}, [nextStep]);

	const onContinueClick = useCallback(() => {
		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[ManualRecordSelect] Continued", { num_meetings_selected: userToggledGuids.size });
		nextStep();
	}, [nextStep, userToggledGuids]);

	const onPrevClick = useCallback(() => {
		if (eventsPage > 1) {
			setEventsPage(eventsPage - 1);
		}
	}, [eventsPage, setEventsPage]);

	const onMoreClick = useCallback(() => {
		if (eventsPage < 3) {
			setEventsPage(eventsPage + 1);
		}
	}, [eventsPage, setEventsPage]);

	const now = useMemo(() => new Date(), []);
	const vars = React.useMemo(() => {
		return {
			start: roundToNearestMinutes(now, { roundingMethod: "floor", nearestTo: 5 }).toISOString(),
			end: endOfDay(addWeeks(now, 2)).toISOString(),
		};
	}, [now]);

	const res = useLazyLoadQuery<ManualRecordSelectPageQuery>(
		graphql`
			query ManualRecordSelectPageQuery($start: DateTime!, $end: DateTime!) {
				viewer {
					user {
						id
						fullName
						internalAttributes {
							greekType
						}
					}
				}
				events(start: $start, end: $end, expansionLimit: 1, includePersonalMeetings: false) {
					edges {
						node {
							id
							guid
							start
							end
							isAllDay
							title
							recurrenceRule
							willBeRecorded(noCache: true)
							conferenceCalls {
								url
							}
							calendar {
								id
							}
						}
					}
				}
			}
		`,
		vars,
		{ fetchPolicy: "store-or-network" },
	);

	const maxNumEvents = 12;
	const numEventsPerPage = 4;

	const baseEvents = compact(res.events.edges)
		.map(edge => edge.node)
		.filter(event => event.start != null && event.end != null)
		.filter(
			event =>
				// This is our best approximation at not showing the event for the getting started stream
				!(event.title == "Get started with meetings" && event.guid.startsWith("fellow")),
		);

	const events = baseEvents
		.filter(event => event.willBeRecorded != "UNKNOWN")
		// it would be nice if typescript would realize that all nodes now have a non null start and end
		.sort((a, b) => (a.start && b.start && a.start >= b.start ? 1 : -1))
		.slice(0, maxNumEvents);

	const numPages = Math.ceil(Math.min(maxNumEvents, events.length) / numEventsPerPage);

	const recordingStatusUnknownEvents = baseEvents.filter(event => event.willBeRecorded == "UNKNOWN");

	if (recordingStatusUnknownEvents) {
		dispatchAnalyticsTrack("[ManualRecordSelect] Found events with UNKNOWN recording status", {
			guids: recordingStatusUnknownEvents.map(event => event.guid),
		});
	}

	// if we're not going to show the toggle for any events, skip this step
	// NOTE: This is coupled to the logic above in ManualRecordSelectEvent for setting recordingIcon to the toggle
	const shouldShowToggle = (event: BaseEvent) => {
		if (event.willBeRecorded == "NO") return true;
		if (event.willBeRecorded == "YES" && userToggledGuids.has(event.guid)) return true;
		return false;
	};

	if (!events.some(event => shouldShowToggle(event))) {
		dispatchAnalyticsTrack("[ManualRecordSelect] Skipping manual record select page because no events to toggle");
		nextStep();
	}

	useEffect(() => {
		localStorage.setItem(LOCAL_STORAGE_USER_TOGGLED_GUIDS_KEY, JSON.stringify(Array.from(userToggledGuids)));
	}, [userToggledGuids]);

	const moreDisabled = useMemo(() => {
		return eventsPage >= numPages;
	}, [eventsPage, numPages]);

	const prevDisabled = useMemo(() => {
		return eventsPage <= 1;
	}, [eventsPage]);

	const greekType = res.viewer.user.internalAttributes?.greekType;

	return (
		<div className={styles.page}>
			<div className={styles.pageLeft}>
				<div className={styles.headerSection}>
					<div className={styles.title}>Select which meetings you want AI Notes for:</div>
					<div className={styles.subtitle}>You can always change or toggle individual meetings later</div>
				</div>
				{events.slice((eventsPage - 1) * numEventsPerPage, eventsPage * numEventsPerPage).map(event => (
					<ManualRecordSelectEvent
						key={event.id}
						event={event}
						viewer={res.viewer}
						userToggledGuids={userToggledGuids}
						updateUserToggledGuids={setUserToggledGuids}
					/>
				))}
				<div className={styles.navButtonContainer}>
					<Link onClick={prevDisabled ? undefined : onPrevClick} className={cx({ [styles.disabled]: prevDisabled })}>
						<div className={styles.navButton}>
							<FontAwesomeIcon icon={faChevronLeft} className={styles.prevChevron} />
							Prev
						</div>
					</Link>
					<Link onClick={moreDisabled ? undefined : onMoreClick} className={cx({ [styles.disabled]: moreDisabled })}>
						<div className={styles.navButton}>
							More suggestions
							<FontAwesomeIcon icon={faChevronRight} className={styles.nextChevron} />
						</div>
					</Link>
				</div>
				<div className={styles.buttonsContainer}>
					{greekType !== "ALPHA" && <Button onClick={onNotNowClick}>Not now</Button>}
					<Tooltip
						disabled={userToggledGuids.size > 0 || greekType !== "ALPHA"}
						content="You must select at least one meeting to record to continue."
					>
						<div>
							<Button onClick={onContinueClick} disabled={userToggledGuids.size < 1} variant="primary">
								Continue
							</Button>
						</div>
					</Tooltip>
				</div>
			</div>
			<div className={styles.pageRight}>
				<img src={mockNotesImg} alt="AI Notes" />
			</div>
		</div>
	);
}
